/**
 * LiveTimeline — vista narrativa de la sesión (Fase 42).
 *
 * A diferencia del Waterfall (operativo, barras absolutas), agrupa los spans
 * por turn y los lista cronológicamente como un "chat con observabilidad".
 * Cada evento se renderea como una fila chip-compacta: tipo + provider/modelo
 * + latencia + costo. Click en una fila abre el SpanDetailDrawer lateral.
 *
 * Polling (F3): se alimenta de `/admin/sessions/:id/timeline` cada 2s mientras
 * la sesión está abierta. F4 lo reemplaza con SSE para que los spans
 * in-flight aparezcan en vivo en lugar de esperar el `.end()`.
 *
 * Auto-scroll: por default sigue al evento más reciente. Si el user
 * scrollea hacia arriba (>= 80px del fondo), pausa el auto-scroll y muestra
 * un botón "saltar al final".
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import type { TimelineSpan } from '../../api/admin.js';
import { kindColor } from '../../lib/spanKind.js';
import {
  formatDurationMs,
  nanoToUsdCompact,
  shortId,
} from '../../lib/format.js';
import { preferRawCost } from '../../lib/cost-display.js';
import { summarizeTurns, type TurnSummary } from '../../lib/session-stats.js';
import { useLiveClock } from '../../lib/useLiveClock.js';

const CONTAINER_KINDS = new Set<string>(['session', 'turn', 'composite', 'job']);

/**
 * Cap defensivo de spans renderizados (F7). El typecast natural de una
 * sesión cabe holgado; cap evita que un trace con sampling muy laxo +
 * sesión muy larga (>30min) degrade el render. Cuando se supera, mostramos
 * un banner con "ver todo".
 */
const RENDER_CAP = 1500;

/**
 * Catálogo visual de kinds que mostramos en la barra de filtros. Los que
 * tienen `pending=true` aún no emiten spans (a habilitarse en F5) — los
 * dejamos visibles deshabilitados para señalar el roadmap.
 */
interface KindOption {
  kind: string;
  icon: string;
  label: string;
  pending?: boolean;
}
const KIND_OPTIONS: ReadonlyArray<KindOption> = [
  { kind: 'llm', icon: '🧠', label: 'LLM' },
  { kind: 'stt', icon: '🎤', label: 'STT' },
  { kind: 'tts', icon: '🔊', label: 'TTS' },
  { kind: 'tool_call', icon: '🔧', label: 'Tools' },
  { kind: 'pronunciation', icon: '🗣️', label: 'PA' },
  // F5.2 — memory.query ya emite kind=retrieval con sessionId heredado.
  { kind: 'retrieval', icon: '⚡', label: 'Memory' },
  // F5.2 — guardrail spans aparecen cuando una policy bloquea un tool.
  { kind: 'guardrail', icon: '🛡️', label: 'Guardrail' },
  // Routing: los LLM spans ya incluyen provider/model en attrs; sin span
  // dedicado por ahora. Pending hasta wireado del resolver al bus.
  { kind: 'routing', icon: '🎯', label: 'Routing', pending: true },
];
const DEFAULT_KINDS = new Set<string>([
  'llm',
  'stt',
  'tts',
  'tool_call',
  'pronunciation',
  'retrieval',
  'guardrail',
]);
const FILTER_STORAGE_KEY = 'admin.live.filters.v1';

interface StoredFilters {
  enabledKinds: string[];
  onlyErrors: boolean;
  criticalPath: boolean;
}

function loadFilters(): {
  enabledKinds: Set<string>;
  onlyErrors: boolean;
  criticalPath: boolean;
} {
  if (typeof window === 'undefined') {
    return { enabledKinds: new Set(DEFAULT_KINDS), onlyErrors: false, criticalPath: false };
  }
  try {
    const raw = window.localStorage.getItem(FILTER_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<StoredFilters>;
      return {
        enabledKinds: new Set(parsed.enabledKinds ?? Array.from(DEFAULT_KINDS)),
        onlyErrors: Boolean(parsed.onlyErrors),
        criticalPath: Boolean(parsed.criticalPath),
      };
    }
  } catch {
    // fall through to defaults
  }
  return { enabledKinds: new Set(DEFAULT_KINDS), onlyErrors: false, criticalPath: false };
}

function persistFilters(filters: StoredFilters): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // localStorage might be unavailable (private mode, quota). Best effort.
  }
}

export interface LiveTimelineProps {
  spans: ReadonlyArray<TimelineSpan>;
  /** Cuando la sesión está en curso, marcamos visualmente el último turn. */
  sessionIsLive: boolean;
  selectedSpanId: string | null;
  onSelectSpan: (spanId: string | null) => void;
}

export function LiveTimeline({
  spans,
  sessionIsLive,
  selectedSpanId,
  onSelectSpan,
}: LiveTimelineProps): ReactElement {
  const initial = useMemo(() => loadFilters(), []);
  const [enabledKinds, setEnabledKinds] = useState<Set<string>>(initial.enabledKinds);
  const [onlyErrors, setOnlyErrors] = useState<boolean>(initial.onlyErrors);
  const [criticalPath, setCriticalPath] = useState<boolean>(initial.criticalPath);

  // Persistir cada cambio. El render extra es trivial; localStorage es sync.
  useEffect(() => {
    persistFilters({
      enabledKinds: Array.from(enabledKinds),
      onlyErrors,
      criticalPath,
    });
  }, [enabledKinds, onlyErrors, criticalPath]);

  // Auto-scroll al fondo cuando llegan eventos nuevos, salvo que el user
  // haya scrolleado hacia arriba.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [autoScroll, setAutoScroll] = useState<boolean>(true);
  const handleScroll = useCallback((): void => {
    const el = scrollRef.current;
    if (!el) return;
    const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setAutoScroll(fromBottom < 80);
  }, []);
  useEffect(() => {
    if (!autoScroll) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [autoScroll, spans]);

  // F7 — cap defensivo. Si superamos RENDER_CAP, tomamos los últimos N
  // (los más recientes son los que más le interesan al admin) y marcamos
  // truncado. Pasamos el cap antes de hacer summarizeTurns para abaratar.
  const [showAll, setShowAll] = useState<boolean>(false);
  const truncated = spans.length > RENDER_CAP && !showAll;
  const visibleSpans = useMemo<ReadonlyArray<TimelineSpan>>(() => {
    if (!truncated) return spans;
    return spans.slice(spans.length - RENDER_CAP);
  }, [spans, truncated]);

  const turns = useMemo<TurnSummary[]>(() => summarizeTurns(visibleSpans), [visibleSpans]);
  const byId = useMemo<Map<string, TimelineSpan>>(() => {
    const m = new Map<string, TimelineSpan>();
    for (const s of visibleSpans) m.set(s.id, s);
    return m;
  }, [visibleSpans]);

  // Tick para mostrar "live N s" en el último turn cuando la sesión está
  // en curso. Sólo arranca el interval si hay un turn live.
  const lastTurn = turns[turns.length - 1] ?? null;
  const lastTurnLive = sessionIsLive && lastTurn !== null;
  const liveNow = useLiveClock(1000, lastTurnLive);

  const toggleKind = useCallback((kind: string): void => {
    setEnabledKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }, []);

  // Spans "background" (no pertenecen a ningún turn). Mostrados en una
  // sección aparte al final — distill, jobs, spans de boot.
  const backgroundSpans = useMemo<TimelineSpan[]>(() => {
    if (turns.length === 0) return [];
    const inTurns = new Set<string>();
    for (const t of turns) for (const id of t.spanIds) inTurns.add(id);
    return visibleSpans.filter(
      (s) => !inTurns.has(s.id) && !CONTAINER_KINDS.has(s.kind),
    );
  }, [visibleSpans, turns]);

  if (spans.length === 0) {
    return (
      <div className="live-tl-empty" data-testid="live-tl-empty">
        <p>Aún no hay eventos persistidos para esta sesión.</p>
        <p className="muted">
          {sessionIsLive
            ? 'La sesión está en curso pero ningún span cerró todavía. Esperá unos segundos o revisá que TRACING_DB_SINK_ENABLED=true.'
            : 'La sesión no produjo spans (tracing apagado o sampled out).'}
        </p>
      </div>
    );
  }

  return (
    <div className="live-tl" data-testid="live-tl">
      <div className="live-tl-filters" role="group" aria-label="Filtros del live">
        <div className="live-tl-chips">
          {KIND_OPTIONS.map((opt) => {
            const on = enabledKinds.has(opt.kind);
            const disabled = opt.pending === true;
            return (
              <button
                key={opt.kind}
                type="button"
                className={`chip${on && !disabled ? ' is-on' : ''}${disabled ? ' is-disabled' : ''}`}
                onClick={() => !disabled && toggleKind(opt.kind)}
                title={disabled ? 'Pendiente (F5 — sin spans aún)' : `Mostrar ${opt.label}`}
                disabled={disabled}
                data-testid={`live-chip-${opt.kind}`}
              >
                <span
                  className="chip-dot"
                  style={{ background: kindColor(opt.kind) }}
                  aria-hidden="true"
                />
                <span>{opt.icon}</span>
                <span>{opt.label}</span>
                {disabled ? <span className="tt-tert">·F5</span> : null}
              </button>
            );
          })}
        </div>
        <div className="live-tl-toggles">
          <label className="toggle">
            <input
              type="checkbox"
              checked={onlyErrors}
              onChange={(e) => setOnlyErrors(e.target.checked)}
              data-testid="live-toggle-errors"
            />
            <span className="toggle-slider" />
            Solo errores
          </label>
          <label className="toggle">
            <input
              type="checkbox"
              checked={criticalPath}
              onChange={(e) => setCriticalPath(e.target.checked)}
              data-testid="live-toggle-critical"
            />
            <span className="toggle-slider" />
            Critical path
          </label>
        </div>
      </div>

      <div
        className="live-tl-scroll"
        ref={scrollRef}
        onScroll={handleScroll}
        data-testid="live-tl-scroll"
      >
        {truncated ? (
          <div className="live-tl-truncated" data-testid="live-tl-truncated">
            Mostrando los últimos {RENDER_CAP} de {spans.length} eventos.
            <button
              type="button"
              className="link-btn"
              onClick={() => setShowAll(true)}
              style={{ marginLeft: 8 }}
            >
              Ver todo
            </button>
          </div>
        ) : null}
        {turns.map((turn, idx) => {
          const isLastTurn = idx === turns.length - 1;
          const isTurnLive = isLastTurn && lastTurnLive && byId.get(turn.turnId)?.durationMs === 0;
          // Hueco entre startedAt del turn y "ahora" para vista live —
          // visible en el header de cada bloque.
          const elapsedMs = isTurnLive
            ? Math.max(0, liveNow - new Date(turn.startedAt).getTime())
            : null;
          return (
            <TurnBlock
              key={turn.turnId}
              turn={turn}
              isLive={isTurnLive}
              elapsedMs={elapsedMs}
              spans={spans}
              byId={byId}
              enabledKinds={enabledKinds}
              onlyErrors={onlyErrors}
              criticalPath={criticalPath}
              selectedSpanId={selectedSpanId}
              onSelectSpan={onSelectSpan}
            />
          );
        })}

        {backgroundSpans.length > 0 ? (
          <BackgroundBlock
            spans={backgroundSpans}
            enabledKinds={enabledKinds}
            onlyErrors={onlyErrors}
            selectedSpanId={selectedSpanId}
            onSelectSpan={onSelectSpan}
          />
        ) : null}
      </div>

      {!autoScroll ? (
        <button
          type="button"
          className="live-tl-jump"
          onClick={() => setAutoScroll(true)}
          data-testid="live-tl-jump"
        >
          ↓ Saltar al final
        </button>
      ) : null}
    </div>
  );
}

interface TurnBlockProps {
  turn: TurnSummary;
  isLive: boolean;
  elapsedMs: number | null;
  spans: ReadonlyArray<TimelineSpan>;
  byId: Map<string, TimelineSpan>;
  enabledKinds: Set<string>;
  onlyErrors: boolean;
  criticalPath: boolean;
  selectedSpanId: string | null;
  onSelectSpan: (spanId: string | null) => void;
}

function TurnBlock(props: TurnBlockProps): ReactElement {
  const {
    turn,
    isLive,
    elapsedMs,
    byId,
    enabledKinds,
    onlyErrors,
    criticalPath,
    selectedSpanId,
    onSelectSpan,
  } = props;
  const [collapsed, setCollapsed] = useState<boolean>(false);

  const events = useMemo<TimelineSpan[]>(() => {
    const list: TimelineSpan[] = [];
    for (const id of turn.spanIds) {
      const s = byId.get(id);
      if (!s) continue;
      if (s.id === turn.turnId) continue; // saltamos el container turn
      list.push(s);
    }
    list.sort((a, b) => a.startedAt.localeCompare(b.startedAt));
    return list;
  }, [turn, byId]);

  const visibleEvents = useMemo<TimelineSpan[]>(() => {
    return events.filter((s) => {
      if (!enabledKinds.has(s.kind)) return false;
      if (onlyErrors && !s.isError) return false;
      // En critical path mostramos sólo el path STT → LLM → TTS principal
      // del turno. Heurística simple: tomamos el primero de cada kind del
      // path serial. El resto se atenúa (no se oculta) para no perder
      // contexto del usuario.
      if (criticalPath) {
        const CRITICAL_KINDS = new Set(['stt', 'llm', 'tts']);
        if (!CRITICAL_KINDS.has(s.kind)) return false;
      }
      return true;
    });
  }, [events, enabledKinds, onlyErrors, criticalPath]);

  return (
    <section
      className={`live-tl-turn${isLive ? ' is-live' : ''}${turn.errorCount > 0 ? ' has-error' : ''}`}
      data-testid={`live-tl-turn-${turn.turnIndex}`}
    >
      <header
        className="live-tl-turn-head"
        onClick={() => setCollapsed((c) => !c)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCollapsed((c) => !c);
          }
        }}
      >
        <span className="live-tl-turn-marker">
          {isLive ? <span className="dot-live" /> : <span className="dot" />}
        </span>
        <span className="live-tl-turn-time mono">
          {new Date(turn.startedAt).toLocaleTimeString('es-AR', { hour12: false })}
        </span>
        <span className="live-tl-turn-title">
          Turn {turn.turnIndex}
          {turn.userText ? (
            <span className="live-tl-turn-snippet"> · “{truncate(turn.userText, 60)}”</span>
          ) : null}
        </span>
        <span className="live-tl-turn-meta">
          {isLive ? (
            <span className="badge b-ok">
              <span className="dot-live" /> {formatDurationMs(elapsedMs ?? 0)} en vivo
            </span>
          ) : (
            <>
              <span className="mono">{formatDurationMs(turn.durationMs)}</span>
              <span className="mono">{nanoToUsdCompact(turn.costNano.toString())}</span>
            </>
          )}
          {turn.errorCount > 0 ? (
            <span className="badge b-error">{turn.errorCount} err</span>
          ) : null}
          <span className="live-tl-turn-collapse" aria-hidden="true">
            {collapsed ? '▸' : '▾'}
          </span>
        </span>
      </header>
      {!collapsed ? (
        <ul className="live-tl-events">
          {visibleEvents.map((span) => (
            <EventRow
              key={span.id}
              span={span}
              selected={span.id === selectedSpanId}
              onSelect={() => onSelectSpan(span.id)}
            />
          ))}
          {visibleEvents.length === 0 ? (
            <li className="live-tl-event-empty muted">Sin eventos visibles con estos filtros.</li>
          ) : null}
        </ul>
      ) : null}
    </section>
  );
}

interface BackgroundBlockProps {
  spans: ReadonlyArray<TimelineSpan>;
  enabledKinds: Set<string>;
  onlyErrors: boolean;
  selectedSpanId: string | null;
  onSelectSpan: (spanId: string | null) => void;
}

function BackgroundBlock(props: BackgroundBlockProps): ReactElement {
  const { spans, enabledKinds, onlyErrors, selectedSpanId, onSelectSpan } = props;
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const visible = useMemo<TimelineSpan[]>(() => {
    return spans
      .filter((s) => {
        if (!enabledKinds.has(s.kind)) return false;
        if (onlyErrors && !s.isError) return false;
        return true;
      })
      .sort((a, b) => a.startedAt.localeCompare(b.startedAt));
  }, [spans, enabledKinds, onlyErrors]);

  return (
    <section className="live-tl-bg" data-testid="live-tl-background">
      <header
        className="live-tl-turn-head"
        onClick={() => setCollapsed((c) => !c)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCollapsed((c) => !c);
          }
        }}
      >
        <span className="live-tl-turn-marker">
          <span className="dot" />
        </span>
        <span className="live-tl-turn-title">
          Background <span className="tt-tert">· {visible.length} eventos</span>
        </span>
        <span className="live-tl-turn-meta">
          <span className="live-tl-turn-collapse" aria-hidden="true">
            {collapsed ? '▸' : '▾'}
          </span>
        </span>
      </header>
      {!collapsed ? (
        <ul className="live-tl-events">
          {visible.map((span) => (
            <EventRow
              key={span.id}
              span={span}
              selected={span.id === selectedSpanId}
              onSelect={() => onSelectSpan(span.id)}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

interface EventRowProps {
  span: TimelineSpan;
  selected: boolean;
  onSelect: () => void;
}

const KIND_ICON: Record<string, string> = {
  llm: '🧠',
  stt: '🎤',
  tts: '🔊',
  tool_call: '🔧',
  pronunciation: '🗣️',
  retrieval: '⚡',
  routing: '🎯',
  guardrail: '🛡️',
  http: '🌐',
  db_query: '💾',
  composite: '◆',
};

function EventRow({ span, selected, onSelect }: EventRowProps): ReactElement {
  const provider = typeof span.attributes['provider'] === 'string' ? span.attributes['provider'] : null;
  const model = typeof span.attributes['model'] === 'string' ? span.attributes['model'] : null;
  const cost = preferRawCost(span.providerCostNano, span.costNano);

  return (
    <li
      className={`live-tl-event${selected ? ' is-selected' : ''}${span.isError ? ' has-error' : ''}`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      data-testid={`live-event-${span.id}`}
    >
      <span
        className="live-tl-event-stripe"
        style={{ background: kindColor(span.kind) }}
        aria-hidden="true"
      />
      <span className="live-tl-event-icon" aria-hidden="true">
        {KIND_ICON[span.kind] ?? '•'}
      </span>
      <span className="live-tl-event-kind">{span.kind}</span>
      <span className="live-tl-event-name" title={span.name}>
        {prettifyName(span.name)}
      </span>
      {provider || model ? (
        <span className="live-tl-event-provider tt-sec">
          {[provider, model].filter(Boolean).join(' · ')}
        </span>
      ) : null}
      <span className="live-tl-event-stats mono">
        <span>{formatDurationMs(span.durationMs)}</span>
        {cost ? <span>{nanoToUsdCompact(cost)}</span> : null}
      </span>
      {span.isError ? <span className="badge b-error">err</span> : null}
      <span className="live-tl-event-id mono tt-tert" title={span.id}>
        {shortId(span.id, 6)}
      </span>
      <span className="live-tl-event-expand" aria-hidden="true">▸</span>
    </li>
  );
}

function prettifyName(name: string): string {
  // Quita prefijos comunes para no llenar de ruido la línea.
  return name
    .replace(/^anthropic\.messages\.stream:?/, 'anthropic ')
    .replace(/^openai\.chat\.completions:?/, 'openai ')
    .replace(/^gemini\.generateContent\.stream:?/, 'gemini ')
    .replace(/^elevenlabs\.tts:?/, 'elevenlabs ')
    .replace(/^deepgram\.stt:?/, 'deepgram ')
    .trim();
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1) + '…';
}
